"use client";

import React, { useState } from 'react';
import { Tag, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { CartItem } from './cart-container';
import type { CartData } from '@/types/cart.types';
import {
    useGetCouponsQuery,
    useApplyCouponMutation,
    useRemoveCouponMutation,
    useCheckoutMutation,
    useClearCartMutation,
    useGetCartCreditStatusQuery,
} from '@/app/store/api/cartApi';
import { useGetCartQuery } from '@/app/store/api/cartApi';
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout';
import { useVerifyCartPaymentMutation } from '@/app/store/api/cartApi';
import { useAppSelector } from '@/app/store/hooks';

interface CartOrderSummaryProps {
    cartData: CartData | undefined;     // server cart (undefined if not yet loaded / unauthenticated)
    localItems: CartItem[];             // current product list with local quantities
    isAuthenticated: boolean;
    onCheckout: () => void;             // called when unauthenticated to open login popover
    cartLoading?: boolean;
}

const formatPrice = (amount: number, symbol: string = '₹') => {
    const formatted = new Intl.NumberFormat('en-IN', { 
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
    return `${symbol}${formatted}`;
};

export const CartOrderSummary = ({
    cartData,
    localItems,
    isAuthenticated,
    onCheckout,
    cartLoading,
}: CartOrderSummaryProps) => {
    const router = useRouter();
    const [couponInput, setCouponInput] = useState('');
    const [isCheckingOut, setIsCheckingOut] = useState(false);

    // ── Coupons (auth only) ────────────────────────────────────────
    const selectedCurrency = useAppSelector((state) => state.currency.selectedCurrency);
    const { data: couponsData, isLoading: couponsLoading } = useGetCouponsQuery(selectedCurrency, {
        skip: !isAuthenticated,
    });
    const [applyCoupon, { isLoading: isApplying }] = useApplyCouponMutation();
    const [removeCoupon, { isLoading: isRemoving }] = useRemoveCouponMutation();
    const [clearCart, { isLoading: isClearing }] = useClearCartMutation();

    // ── Checkout ───────────────────────────────────────────────────
    const [checkout] = useCheckoutMutation();
    const [verifyPayment] = useVerifyCartPaymentMutation();
    const { refetch: refetchCart } = useGetCartQuery(selectedCurrency, { skip: !isAuthenticated });
    const { refetch: refetchCredits } = useGetCartCreditStatusQuery(selectedCurrency, { skip: !isAuthenticated });

    // Build our customised openCheckout that goes via /cart/checkout
    const handleBuyNow = async () => {
        if (!isAuthenticated) {
            onCheckout();
            return;
        }

        if (itemCount === 0) {
            toast.error('Your cart is empty. Add some items first.');
            return;
        }

        setIsCheckingOut(true);
        try {
            const checkoutResponse = await checkout(selectedCurrency).unwrap();

            // Load Razorpay
            await loadRazorpayAndOpen(checkoutResponse);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to create order. Please try again.');
        } finally {
            setIsCheckingOut(false);
        }
    };

    const loadRazorpayAndOpen = async (checkoutResponse: any) => {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
            toast.error('Failed to load payment gateway. Please try again.');
            return;
        }

        const razorpayKey = process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID;
        if (!razorpayKey) {
            toast.error('Payment gateway not configured.');
            return;
        }

        const options: RazorpayOptions = {
            key: razorpayKey,
            amount: checkoutResponse.order.amount,
            currency: checkoutResponse.order.currency,
            name: 'Deckwale',
            description: `Cart Purchase — ${formatPrice(checkoutResponse.amount, checkoutResponse.currencySymbol || '₹')}`,
            order_id: checkoutResponse.order.id,
            handler: async (response) => {
                try {
                    await verifyPayment({
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        transactionId: checkoutResponse.transactionId,
                    }).unwrap();

                    // Capture purchased items for the success modal
                    const snapshot = displayItems.map((i) => ({ name: i.name, quantity: i.quantity }));
                    sessionStorage.setItem('deckwale_payment_success_data', JSON.stringify(snapshot));

                    // Refresh cart + credits
                    refetchCart();
                    refetchCredits();

                    // Redirect to home page with success state
                    router.push('/?payment_success=1');
                } catch (err: any) {
                    toast.error(err?.data?.message || 'Payment verification failed. Contact support.');
                }
            },
            theme: { color: '#2563eb' },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', (resp: any) => {
            toast.error(resp.error?.description || 'Payment failed. Please try again.');
        });
        razorpay.open();
    };

    // ── Coupon handlers ────────────────────────────────────────────
    const handleApplyCoupon = async (code: string) => {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) return;
        if (!isAuthenticated) { onCheckout(); return; }

        try {
            await applyCoupon({ code: trimmed }).unwrap();
            setCouponInput('');
            toast.success('Coupon applied!');
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to apply coupon.');
        }
    };

    const handleRemoveCoupon = async () => {
        try {
            await removeCoupon().unwrap();
            toast.success('Coupon removed.');
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to remove coupon.');
        }
    };

    const handleClearCart = async () => {
        try {
            await clearCart().unwrap();
            toast.success('Cart cleared.');
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to clear cart.');
        }
    };

    // ── Derived totals ─────────────────────────────────────────────
    // For authenticated users: use server-computed values
    // For unauthenticated: compute locally for preview
    const serverItemCount = cartData?.itemCount ?? 0;
    const localItemCount = localItems.reduce((s, i) => s + i.quantity, 0);
    const itemCount = isAuthenticated ? serverItemCount : localItemCount;

    const subtotal = isAuthenticated
        ? (cartData?.subtotal ?? 0)
        : localItems.reduce((s, i) => s + i.price * i.quantity, 0);

    const discount = cartData?.discountAmount ?? 0;
    const total = isAuthenticated
        ? (cartData?.total ?? subtotal)
        : subtotal;

    const appliedCoupon = cartData?.appliedCoupon ?? null;
    const coupons = couponsData?.data?.coupons ?? [];

    // ── Empty/auth states ──────────────────────────────────────────
    const EmptyStateBox = () => {
        if (!isAuthenticated) {
            return (
                <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 overflow-hidden">
                        <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775731530651_mr4bjjf98y9_profile.png" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    <button
                        onClick={onCheckout}
                        className="text-slate-800 font-bold bg-slate-100 hover:bg-slate-200 px-5 py-2 rounded-xl text-sm cursor-pointer transition-colors mb-4"
                    >
                        Sign in to Start
                    </button>
                    <p className="text-[13px] text-slate-500 max-w-[240px]">
                        Please login to add items to your cart and proceed with the purchase.
                    </p>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 border border-dashed border-slate-200">
                    <img src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775731765246_t8v4jfyx82l_shopping-cart.png" alt="Empty Cart" className="w-[60%] h-[60%] object-contain opacity-70" />
                </div>
                <h4 className="text-slate-700 font-bold mb-2">Your cart is empty</h4>
                <p className="text-[13px] text-slate-400 max-w-[240px]">
                    Select the tools you need from the left to build your personalized plan.
                </p>
            </div>
        );
    };

    // Which items to show — server cart items when auth, else local items with qty > 0
    const displayItems = isAuthenticated && cartData
        ? cartData.items.map((si) => ({
            id: si.productType,
            name: si.productName,
            quantity: si.quantity,
            lineTotal: si.lineTotal,
            unitPrice: si.unitPrice,
        }))
        : localItems.filter((i) => i.quantity > 0).map((i) => ({
            id: i.id,
            name: i.name,
            quantity: i.quantity,
            lineTotal: i.price * i.quantity,
            unitPrice: i.price,
        }));

    const localSymbolMap: Record<string, string> = { INR: '₹', USD: '$', EUR: '€' };
    const currencySymbol = localSymbolMap[selectedCurrency] || cartData?.currencySymbol || localItems[0]?.currencySymbol || '₹';

    return (
        <div className="flex flex-col gap-6">
            <div className="bg-[#f8fafc] rounded-2xl p-6 border border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 mb-4">Order Summary</h3>

                {/* Coupon Input */}
                <div className="flex flex-col mb-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon(couponInput)}
                            placeholder="Enter coupon code"
                            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400 uppercase"
                        />
                        <button
                            onClick={() => handleApplyCoupon(couponInput)}
                            disabled={isApplying}
                            className="bg-white border border-slate-200 text-slate-700 font-bold text-[13px] px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
                        >
                            {isApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                        </button>
                    </div>
                </div>

                {/* Apply Coupons label row */}
                <div className="flex items-center justify-between border-b mx-[-6px] px-[6px] border-dashed border-slate-200 pb-5 mb-5">
                    <div className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
                        <Tag className="w-4 h-4 text-slate-500 flex-shrink-0 -rotate-90" />
                        Apply Coupons
                    </div>
                    <span className="text-[11px] font-bold text-blue-600 border border-blue-200 bg-white rounded-full px-3 py-1">
                        View More
                    </span>
                </div>

                {/* Empty / loading / content */}
                {cartLoading ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    </div>
                ) : itemCount === 0 ? (
                    <EmptyStateBox />
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xs font-bold text-slate-500">
                                Price Details (<span className="text-black font-bold">{itemCount} Item{itemCount > 1 ? 's' : ''}</span>)
                            </h3>
                            {isAuthenticated && (
                                <button
                                    onClick={handleClearCart}
                                    disabled={isClearing}
                                    className="text-[11px] font-bold text-red-500 hover:text-red-700 transition-colors cursor-pointer disabled:opacity-50"
                                >
                                    {isClearing ? 'Clearing...' : 'Clear Cart'}
                                </button>
                            )}
                        </div>

                        <div className="space-y-3 mb-5 text-[13px] font-medium text-slate-600">
                            {displayItems.map((item) => (
                                <div key={item.id} className="flex justify-between items-center">
                                    <span>
                                        {item.name} <span className="text-black font-bold">x {item.quantity}</span>
                                    </span>
                                    <span className="text-slate-800">{formatPrice(item.lineTotal, currencySymbol)}</span>
                                </div>
                            ))}

                            {appliedCoupon && discount > 0 && (
                                <div className="flex justify-between items-center text-emerald-600">
                                    <span>
                                        Coupon Discount{' '}
                                        <span className="text-blue-500 ml-1">({appliedCoupon.code})</span>
                                    </span>
                                    <span>
                                        - {formatPrice(discount, currencySymbol)}{' '}
                                        <button
                                            onClick={handleRemoveCoupon}
                                            disabled={isRemoving}
                                            className="text-[10px] text-slate-400 hover:text-red-500 ml-1 cursor-pointer disabled:opacity-50"
                                        >
                                            {isRemoving ? '...' : '(Remove)'}
                                        </button>
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-200 pt-5 mb-6">
                            <span className="font-bold text-slate-800 text-[15px]">Total Amount</span>
                            <span className="font-bold text-slate-900 text-[18px]">{formatPrice(total, currencySymbol)}</span>
                        </div>
                    </>
                )}

                <div className="text-center pt-2">
                    <p className="text-[11px] text-slate-500 font-medium mb-3">
                        By placing the order, you agree to Deckwale's{' '}
                        <Link href="/terms-of-use" className="text-blue-600 hover:underline">Terms of Use</Link>
                        {' '}and{' '}
                        <Link href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                    </p>
                    <button
                        onClick={handleBuyNow}
                        disabled={isCheckingOut || (isAuthenticated && itemCount === 0)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-[14px] py-2.5 rounded-xl transition-all shadow-md shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCheckingOut ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Creating order...
                            </span>
                        ) : 'Buy Now'}
                    </button>
                </div>
            </div>

            {/* Available Coupons (auth only, desktop only) */}
            {isAuthenticated && (
                <div className="bg-[#f8fafc] rounded-2xl p-6 border border-slate-100 hidden lg:block">
                    <h3 className="text-xs font-bold text-slate-500 mb-4">Available Coupons</h3>

                    {couponsLoading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        </div>
                    ) : coupons.length === 0 ? (
                        <p className="text-[12px] text-slate-400 text-center py-4">No coupons available right now.</p>
                    ) : (
                        <div className="space-y-3">
                            {coupons.map((coupon) => (
                                <div key={coupon.code} className="bg-white border rounded-xl p-3 shadow-sm border-slate-100 flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${coupon.isApplicable ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                                <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            </div>
                                            <span className="font-bold text-slate-800 text-[13px]">{coupon.code}</span>
                                            <span className="text-emerald-600 text-[11px] font-bold ml-1">{coupon.savingsLabel}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 font-medium">{coupon.description}</p>
                                    </div>
                                    <button
                                        onClick={() => handleApplyCoupon(coupon.code)}
                                        disabled={!coupon.isApplicable || isApplying || !!appliedCoupon}
                                        className="text-[12px] font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer disabled:text-slate-400 disabled:cursor-not-allowed"
                                    >
                                        Apply
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Static coupon cards for unauthenticated preview */}
            {!isAuthenticated && (
                <div className="bg-[#f8fafc] rounded-2xl p-6 border border-slate-100 hidden lg:block">
                    <h3 className="text-xs font-bold text-slate-500 mb-4">Available Coupons</h3>
                    <div className="space-y-3">
                        {[
                            { code: 'DECK50', savings: `You Save ${currencySymbol}50`, desc: `Get ${currencySymbol}50 off on order above ${currencySymbol}499` },
                            { code: 'DECK150', savings: `You Save ${currencySymbol}150`, desc: `Get ${currencySymbol}150 off on order above ${currencySymbol}999` },
                            { code: 'DECK700', savings: `You Save ${currencySymbol}700`, desc: `Get ${currencySymbol}700 off on order above ${currencySymbol}4999` },
                        ].map((c) => (
                            <div key={c.code} className="bg-white border rounded-xl p-3 shadow-sm border-slate-100 flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                                            <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                        <span className="font-bold text-slate-800 text-[13px]">{c.code}</span>
                                        <span className="text-emerald-600 text-[11px] font-bold ml-1">{c.savings}</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 font-medium">{c.desc}</p>
                                </div>
                                <button onClick={onCheckout} className="text-[12px] font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer">
                                    Apply
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Razorpay script loader ─────────────────────────────────────────
function loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') return resolve(false);
        if ((window as any).Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}
